// Tab global de Tickets (Fase 4 de la integración CRM en Operations). Lista los
// tickets del CRM (GET /tickets) con filtros de pipeline/stage/estado/asignado y
// búsqueda de texto libre `q` (debounce ~300ms), tabla paginada por page/total y
// alta de tickets reutilizando el modal compartido. Todo el contenido va
// envuelto en CrmConnectGate (auth por cookie del CRM).

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCrmPagedList, useCrmResource } from "../../hooks/useCrmResource";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  listTicketPipelines,
  listTickets,
  listTicketStages,
  PRIORITY_LABELS,
} from "../../api/crmTickets";
import {
  TicketPipeline,
  TicketPriority,
  TicketStage,
  TicketState,
  TicketSummary,
} from "../../api/crmTypes";
import { CrmConnectGate } from "../../context/CrmSessionContext";
import { Select } from "../../components/FormControls";
import CreateTicketModal from "../../components/crm/CreateTicketModal";
import Pill from "../LeadDetail/components/Pill";
import {
  FilterCell,
  Td,
  Th,
  errorBox,
  inputStyle,
  pagerBtn,
  retryInline,
  viewToggleLink,
} from "../../components/crm/TableChrome";
import { fmtDate, fmtDateTime } from "../../types/promos";
import { TICKET_INCIDENT_TYPES } from "../../api/crmTicketTypification";

const PAGE_SIZE = 50;

const INCIDENT_TYPE_OPTIONS = TICKET_INCIDENT_TYPES.map((it) => ({
  value: it,
  label: it,
}));

const STATE_OPTIONS: { value: TicketState; label: string }[] = [
  { value: "open", label: "Abierto" },
  { value: "closed", label: "Cerrado" },
];

const PRIORITY_SCHEME: Record<
  TicketPriority,
  "muted" | "blue" | "amber" | "purple"
> = {
  low: "muted",
  medium: "blue",
  high: "amber",
  urgent: "purple",
};

export default function Tickets() {
  return (
    <div style={{ padding: "1.5rem" }}>
      <CrmConnectGate>
        <TicketsInner />
      </CrmConnectGate>
    </div>
  );
}

function TicketsInner() {
  const navigate = useNavigate();

  // Filtros.
  const [pipelineId, setPipelineId] = useState("");
  const [stageId, setStageId] = useState("");
  const [state, setState] = useState("");
  const [incidentType, setIncidentType] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [search, setSearch] = useState("");
  // Rescate de un archivado por error (archived='only'). El listado normal no
  // los muestra: el default del BFF es 'exclude'.
  const [archivedOnly, setArchivedOnly] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);

  // Debounce ~300ms de los inputs de texto (búsqueda y email asignado): el
  // valor visible actualiza al instante, pero el refetch solo dispara cuando el
  // usuario deja de teclear. `assignedTo` también se debounce para no refetchear
  // por cada tecla.
  const q = useDebouncedValue(search.trim(), 300);
  const assignedToQ = useDebouncedValue(assignedTo.trim(), 300);

  // Pipelines para el filtro (y para resolver stages dependientes / precargar
  // el modal de alta sin que este vuelva a fetchear).
  const pipelines = useCrmResource(
    () => listTicketPipelines().then((ps) => ps.filter((p) => !p.archived)),
    [],
    "No se pudieron cargar los pipelines",
  );

  // Stages del pipeline seleccionado (filtro dependiente).
  const stages = useCrmResource<TicketStage[]>(
    () =>
      pipelineId
        ? listTicketStages(pipelineId).then((ss) => ss.filter((s) => !s.archived))
        : Promise.resolve([]),
    [pipelineId],
    "No se pudieron cargar las etapas",
  );

  // Lista paginada: el hook posee page/total y resetea a la página 1 cuando
  // cambian los filtros (deps sin page). Usa el pageSize del server para el
  // rango y hasNext.
  const list = useCrmPagedList<TicketSummary>(
    (page) =>
      listTickets({
        page,
        limit: PAGE_SIZE,
        ...(pipelineId ? { pipelineId } : {}),
        ...(stageId ? { stageId } : {}),
        ...(state ? { state: state as TicketState } : {}),
        ...(incidentType ? { incidentType } : {}),
        ...(assignedToQ ? { assignedTo: assignedToQ } : {}),
        ...(q ? { q } : {}),
        ...(archivedOnly ? { archived: "only" as const } : {}),
      }),
    [pipelineId, stageId, state, incidentType, assignedToQ, q, archivedOnly],
    "No se pudieron cargar los tickets",
  );

  const pipelineOptions = useMemo(
    () => (pipelines.data ?? []).map((p: TicketPipeline) => ({ value: p.id, label: p.label })),
    [pipelines.data],
  );
  const stageOptions = useMemo(
    () => (stages.data ?? []).map((s) => ({ value: s.id, label: s.label })),
    [stages.data],
  );

  const { items, total, page, setPage, pageStart, pageEnd, hasPrev, hasNext } = list;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "1.1rem", color: "#2A3F54" }}>Tickets</h1>
        <Link to="/operations/tickets/board" style={viewToggleLink}>
          Tablero
        </Link>
        <span style={{ color: "#73879C", fontSize: ".82rem" }}>
          {list.loading && items.length === 0 ? "Cargando…" : `${total} tickets`}
        </span>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={() => setCreateOpen(true)} style={addBtn}>
          + Crear ticket
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: "0.6rem",
          marginBottom: "1rem",
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <FilterCell label="Pipeline">
          <Select
            value={pipelineId}
            onChange={(v) => {
              setPipelineId(v);
              setStageId("");
            }}
            options={pipelineOptions}
            placeholder="Todos"
          />
        </FilterCell>
        <FilterCell label="Etapa">
          <Select
            value={stageId}
            onChange={setStageId}
            options={stageOptions}
            placeholder={pipelineId ? "Todas" : "Elige pipeline"}
            disabled={!pipelineId || stages.loading}
          />
        </FilterCell>
        <FilterCell label="Estado">
          <Select
            value={state}
            onChange={setState}
            options={STATE_OPTIONS}
            placeholder="Todos"
          />
        </FilterCell>
        <FilterCell label="Tipo incidencia">
          <Select
            value={incidentType}
            onChange={setIncidentType}
            options={INCIDENT_TYPE_OPTIONS}
            placeholder="Todos"
          />
        </FilterCell>
        <FilterCell label="Asignado (email)">
          <input
            type="text"
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            placeholder="agente@life5.com"
            style={inputStyle}
          />
        </FilterCell>
        <FilterCell label="Buscar">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Asunto o descripción…"
            style={inputStyle}
          />
        </FilterCell>
        {/* Rescate. Al marcarlo se LIMPIA el Estado: buscar un archivado es
            buscar en todas las etapas, y un `state` puesto escondería justo el
            que está en una etapa que no miras (DECISIONS.md 2026-07-16). */}
        <label style={archivedToggle}>
          <input
            type="checkbox"
            checked={archivedOnly}
            onChange={(e) => {
              setArchivedOnly(e.target.checked);
              if (e.target.checked) setState("");
            }}
          />
          Solo archivados
        </label>
      </div>

      {list.error && (
        <div style={errorBox}>
          {list.error}{" "}
          <button type="button" onClick={list.reload} style={retryInline}>
            Reintentar
          </button>
        </div>
      )}

      <div
        style={{
          background: "white",
          borderRadius: "0.55rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "auto",
        }}
      >
        <table
          style={{
            width: "100%",
            minWidth: 960,
            borderCollapse: "collapse",
            fontSize: ".83rem",
          }}
        >
          <thead>
            <tr style={{ background: "#f8f9fb" }}>
              <Th width="24%">Asunto</Th>
              <Th width={80}>Lead</Th>
              <Th width="18%">Pipeline / Etapa</Th>
              <Th width="14%">Tipificación</Th>
              <Th width={90}>Estado</Th>
              <Th width="16%">Asignado</Th>
              <Th width={110}>Prioridad</Th>
              <Th width={130}>Actividad</Th>
            </tr>
          </thead>
          <tbody>
            {!list.loading && !list.error && items.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  style={{ padding: "1.5rem", textAlign: "center", color: "#73879C" }}
                >
                  Sin resultados.
                </td>
              </tr>
            )}
            {items.map((t) => (
              <TicketRow
                key={t.id}
                ticket={t}
                onOpen={() => navigate(`/operations/tickets/${t.id}`)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          marginTop: "0.8rem",
          fontSize: ".8rem",
          color: "#73879C",
        }}
      >
        <span>{`${pageStart}-${pageEnd} de ${total}`}</span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={!hasPrev || list.loading}
          style={pagerBtn(!hasPrev || list.loading)}
        >
          Anterior
        </button>
        <button
          type="button"
          onClick={() => setPage((p) => p + 1)}
          disabled={!hasNext || list.loading}
          style={pagerBtn(!hasNext || list.loading)}
        >
          Siguiente
        </button>
      </div>

      {createOpen && (
        <CreateTicketModal
          pipelines={pipelines.data ?? undefined}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            list.reload();
          }}
        />
      )}
    </div>
  );
}

function TicketRow({
  ticket,
  onOpen,
}: {
  ticket: TicketSummary;
  onOpen: () => void;
}) {
  const closed = ticket.stageState === "closed";
  return (
    <tr
      onClick={onOpen}
      style={{ borderTop: "1px solid #f1f3f5", cursor: "pointer", opacity: closed ? 0.75 : 1 }}
    >
      <Td truncate maxWidth={340}>
        <strong title={ticket.subject}>{ticket.subject}</strong>
      </Td>
      <Td>
        {ticket.leadId ? (
          // Link interno a la ficha del lead; el leadId del ticket ES el ulid.
          // Detenemos la propagación para no abrir además el detalle del ticket.
          <Link
            to={`/leads/${ticket.leadId}`}
            onClick={(e) => e.stopPropagation()}
            style={{ color: "#0069d9", fontWeight: 600 }}
          >
            Ver lead
          </Link>
        ) : (
          <span style={{ color: "#adb5bd" }}>—</span>
        )}
      </Td>
      <Td>
        <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
          <Pill label={ticket.pipelineLabel} scheme="purple" />
          <Pill label={ticket.stageLabel} scheme={closed ? "muted" : "blue"} />
        </div>
      </Td>
      <Td truncate maxWidth={180}>
        {ticket.typification || ticket.incidentType ? (
          <span
            title={
              [ticket.incidentType, ticket.typification]
                .filter(Boolean)
                .join(" · ")
            }
          >
            {ticket.typification ?? ticket.incidentType}
          </span>
        ) : (
          <span style={{ color: "#adb5bd" }}>—</span>
        )}
      </Td>
      <Td>
        <Pill label={closed ? "Cerrado" : "Abierto"} scheme={closed ? "muted" : "green"} />
      </Td>
      <Td truncate maxWidth={200}>
        {ticket.assignedTo ? (
          <span title={ticket.assignedTo}>{ticket.assignedTo}</span>
        ) : (
          <span style={{ color: "#adb5bd" }}>Sin asignar</span>
        )}
      </Td>
      <Td>
        {ticket.priority ? (
          <Pill
            label={PRIORITY_LABELS[ticket.priority]}
            scheme={PRIORITY_SCHEME[ticket.priority]}
          />
        ) : (
          <span style={{ color: "#adb5bd" }}>—</span>
        )}
      </Td>
      <Td>
        <span title={`Creado: ${fmtDateTime(ticket.createdAt)}`}>
          {fmtDate(ticket.lastActivityAt)}
        </span>
      </Td>
    </tr>
  );
}

// Alineado con la base de los <select>/<input> de filtro (la fila usa
// align-items:flex-end), no con su etiqueta: es un filtro más de la misma fila.
const archivedToggle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.35rem",
  paddingBottom: "0.45rem",
  color: "#73879C",
  fontSize: ".8rem",
  fontWeight: 600,
  cursor: "pointer",
  userSelect: "none",
};

const addBtn: React.CSSProperties = {
  padding: "0.45rem 0.9rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

